import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography, Button, Box, Alert, CircularProgress, TextField,
  List, ListItem, ListItemAvatar, ListItemText, Avatar,
  IconButton, InputAdornment, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ModalWrapper from '../components/ModalWrapper';
import { Grocery } from '../types/Grocery';
import { ShoppingListGrocery } from '../types/ShoppingListGrocery';
import { apiFetch } from '../apiFetch';

interface BulkAddGroceriesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (items: ShoppingListGrocery[]) => void;
  shoppingListId: number;
  existingGroceryIds: number[];
}

interface GroceryQuantity {
  grocery: Grocery;
  quantity: number;
}

const BulkAddGroceriesModal: React.FC<BulkAddGroceriesModalProps> = ({
  open,
  onClose,
  onSuccess,
  shoppingListId,
  existingGroceryIds,
}) => {
  const [availableGroceries, setAvailableGroceries] = useState<Grocery[]>([]);
  const [quantities, setQuantities] = useState<Map<number, number>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Fetch available groceries
  useEffect(() => {
    if (!open) return;

    const fetchGroceries = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const data = await apiFetch(`${apiUrl}/api/groceries/`);
        // Filter out groceries already in the list
        const filtered = data.filter((g: Grocery) => !existingGroceryIds.includes(g.id));
        // Sort alphabetically
        filtered.sort((a: Grocery, b: Grocery) => a.name.localeCompare(b.name));
        setAvailableGroceries(filtered);
      } catch (err: any) {
        setApiError(err.message || 'Failed to load groceries.');
      } finally {
        setLoading(false);
      }
    };

    fetchGroceries();
    // Reset state when modal opens
    setQuantities(new Map());
    setSearchQuery('');
  }, [apiUrl, open, existingGroceryIds]);

  // Filter groceries based on search
  const filteredGroceries = useMemo(() => {
    if (!searchQuery.trim()) return availableGroceries;

    const query = searchQuery.toLowerCase();
    return availableGroceries.filter(g =>
      g.name.toLowerCase().includes(query) ||
      (g.brand && g.brand.toLowerCase().includes(query)) ||
      (g.category && g.category.toLowerCase().includes(query))
    );
  }, [availableGroceries, searchQuery]);

  // Get groceries with quantities > 0
  const selectedGroceries = useMemo((): GroceryQuantity[] => {
    const selected: GroceryQuantity[] = [];
    quantities.forEach((qty, groceryId) => {
      if (qty > 0) {
        const grocery = availableGroceries.find(g => g.id === groceryId);
        if (grocery) {
          selected.push({ grocery, quantity: qty });
        }
      }
    });
    return selected.sort((a, b) => a.grocery.name.localeCompare(b.grocery.name));
  }, [quantities, availableGroceries]);

  const totalItems = selectedGroceries.reduce((sum, item) => sum + item.quantity, 0);

  const handleQuantityChange = (groceryId: number, delta: number) => {
    setQuantities(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(groceryId) || 0;
      const newValue = Math.max(0, current + delta);
      if (newValue === 0) {
        newMap.delete(groceryId);
      } else {
        newMap.set(groceryId, newValue);
      }
      return newMap;
    });
  };

  const handleSetQuantity = (groceryId: number, value: string) => {
    const numValue = parseInt(value, 10);
    setQuantities(prev => {
      const newMap = new Map(prev);
      if (isNaN(numValue) || numValue <= 0) {
        newMap.delete(groceryId);
      } else {
        newMap.set(groceryId, numValue);
      }
      return newMap;
    });
  };

  const handleSubmit = async () => {
    if (selectedGroceries.length === 0) return;

    setSaving(true);
    setApiError(null);

    try {
      const savedItems: ShoppingListGrocery[] = [];

      for (const item of selectedGroceries) {
        const savedItem = await apiFetch(
          `${apiUrl}/api/shopping-lists/${shoppingListId}/groceries/`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              grocery: item.grocery.id,
              quantity: item.quantity,
            }),
          }
        );
        savedItems.push(savedItem);
      }

      onSuccess(savedItems);
      onClose();
    } catch (err: any) {
      setApiError(err.message || 'Failed to add groceries to list.');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAll = () => {
    setQuantities(new Map());
  };

  const formatGrocerySecondary = (grocery: Grocery): string => {
    const parts = [];
    if (grocery.brand) parts.push(grocery.brand);
    if (grocery.packaging_size) parts.push(grocery.packaging_size);
    if (grocery.category) parts.push(grocery.category);
    return parts.join(' • ');
  };

  const noGroceriesAvailable = !loading && availableGroceries.length === 0;

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <Typography variant="h6" gutterBottom>
        Add Groceries to List
      </Typography>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError(null)}>
          {apiError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : noGroceriesAvailable ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          All your groceries are already in this list. Add more groceries in the Groceries section first.
        </Alert>
      ) : (
        <>
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search groceries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Selected count */}
          {selectedGroceries.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Chip
                label={`${selectedGroceries.length} item${selectedGroceries.length !== 1 ? 's' : ''} selected (${totalItems} total)`}
                color="primary"
                size="small"
              />
              <Button size="small" onClick={handleClearAll}>
                Clear All
              </Button>
            </Box>
          )}

          {/* Grocery list */}
          <List sx={{ maxHeight: 400, overflow: 'auto', bgcolor: 'background.paper' }}>
            {filteredGroceries.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="No groceries found"
                  secondary="Try a different search term"
                  sx={{ textAlign: 'center' }}
                />
              </ListItem>
            ) : (
              filteredGroceries.map((grocery) => {
                const quantity = quantities.get(grocery.id) || 0;
                return (
                  <ListItem
                    key={grocery.id}
                    sx={{
                      bgcolor: quantity > 0 ? 'action.selected' : 'transparent',
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(grocery.id, -1)}
                          disabled={quantity === 0}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          size="small"
                          value={quantity || ''}
                          onChange={(e) => handleSetQuantity(grocery.id, e.target.value)}
                          sx={{ width: 50 }}
                          inputProps={{
                            style: { textAlign: 'center', padding: '4px' },
                            min: 0,
                          }}
                          placeholder="0"
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(grocery.id, 1)}
                          color={quantity === 0 ? 'primary' : 'default'}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={grocery.image_thumbnail || grocery.image || undefined}
                        variant="rounded"
                        sx={{ width: 40, height: 40 }}
                      >
                        {grocery.name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={grocery.name}
                      secondary={formatGrocerySecondary(grocery)}
                      sx={{ pr: 12 }}
                    />
                  </ListItem>
                );
              })
            )}
          </List>
        </>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || selectedGroceries.length === 0 || noGroceriesAvailable}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
        >
          {saving
            ? 'Adding...'
            : `Add ${selectedGroceries.length > 0 ? `${selectedGroceries.length} Item${selectedGroceries.length !== 1 ? 's' : ''}` : 'to List'}`
          }
        </Button>
      </Box>
    </ModalWrapper>
  );
};

export default BulkAddGroceriesModal;